import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Lightbulb, 
  Plus, 
  Clock, 
  CheckCircle2, 
  Sparkles,
  MessageSquare,
  Rocket
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, acknowledged: 0 });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newSuggestion, setNewSuggestion] = useState({
    title: '',
    description: '',
    category: 'content_idea'
  });

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/suggestions`);
      setSuggestions(response.data.suggestions);
      setCounts(response.data.counts);
    } catch (error) {
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newSuggestion.title.trim() || !newSuggestion.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/suggestions`, newSuggestion);
      toast.success('Your idea has been submitted!');
      setIsDialogOpen(false);
      setNewSuggestion({ title: '', description: '', category: 'content_idea' });
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'acknowledged':
        return (
          <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Acknowledged
          </Badge>
        );
      case 'implemented':
        return (
          <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
            <Rocket className="w-3 h-3 mr-1" />
            Implemented
          </Badge>
        );
      case 'declined':
        return (
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">
            Reviewed
          </Badge>
        );
      default:
        return null;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'feature':
        return <Sparkles className="w-4 h-4 text-purple-500" />;
      case 'improvement':
        return <Rocket className="w-4 h-4 text-[#556B2F]" />;
      case 'content_idea':
        return <Lightbulb className="w-4 h-4 text-amber-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-suggestions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Your Ideas
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Share your suggestions with the agency
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#556B2F] hover:bg-[#3d4f22] text-white w-full sm:w-auto" data-testid="suggest-idea-btn">
              <Plus className="w-4 h-4 mr-2" />
              Suggest an Idea
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Share Your Idea
              </DialogTitle>
              <DialogDescription>
                Have a content idea or suggestion? Share it with the agency team.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newSuggestion.title}
                  onChange={(e) => setNewSuggestion({ ...newSuggestion, title: e.target.value })}
                  placeholder="Brief title for your idea"
                  required
                  data-testid="suggestion-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={newSuggestion.category} 
                  onValueChange={(v) => setNewSuggestion({ ...newSuggestion, category: v })}
                >
                  <SelectTrigger data-testid="suggestion-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="content_idea">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        Content Idea
                      </div>
                    </SelectItem>
                    <SelectItem value="feature">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        Feature Request
                      </div>
                    </SelectItem>
                    <SelectItem value="improvement">
                      <div className="flex items-center gap-2">
                        <Rocket className="w-4 h-4 text-[#556B2F]" />
                        Improvement
                      </div>
                    </SelectItem>
                    <SelectItem value="other">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-500" />
                        Other
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newSuggestion.description}
                  onChange={(e) => setNewSuggestion({ ...newSuggestion, description: e.target.value })}
                  placeholder="Describe your idea in detail..."
                  rows={4}
                  required
                  data-testid="suggestion-description-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting} 
                  className="flex-1 bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                  data-testid="submit-suggestion-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Idea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{counts.pending}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#d8e5b8] dark:bg-[#556B2F]/15 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#3d4f22] dark:text-[#b8cf88]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{counts.acknowledged}</p>
              <p className="text-sm text-slate-500">Acknowledged</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suggestions List */}
      {suggestions.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No ideas yet
            </h3>
            <p className="text-slate-500 mb-4">
              Share your first content idea or suggestion with the agency
            </p>
            <Button 
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Suggest an Idea
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <Card 
              key={suggestion.id} 
              className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              data-testid={`suggestion-${suggestion.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getCategoryIcon(suggestion.category)}
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {suggestion.title}
                      </h3>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                      {suggestion.description}
                    </p>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(suggestion.status)}
                      <span className="text-xs text-slate-400">
                        {new Date(suggestion.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {suggestion.agency_notes && (
                      <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Agency Response:</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">{suggestion.agency_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientSuggestions;
